/**
 * For some reason, moment locale configuration does not work
 * when locales are imported from "moment/locale/*" with Vite.
 * @see https://github.com/moment/moment/issues/5926
 */
import 'moment/dist/locale/en-gb';
import 'moment/dist/locale/de';
import 'moment/dist/locale/de-at';
import 'moment/dist/locale/de-ch';
import 'moment/dist/locale/fr-ch';
import 'moment/dist/locale/fr';

import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { loadStripe } from '@stripe/stripe-js';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { createBrowserRouter, Route, RouterProvider, Routes } from 'react-router-dom';
import type { Store } from 'redux';
import type { Persistor } from 'redux-persist/es/types';
import { PersistGate } from 'redux-persist/integration/react';
import styled from 'styled-components';

import { AuthenticationContextProvider } from '@wecasa/auth/src/authentication-context';

import { ConfigProvider } from '@wecasa/common/src/providers/config-provider';
import { ExperimentProvider } from '@wecasa/common/src/providers/experiment-provider';
import { UniversesProvider } from '@wecasa/common/src/providers/universes-provider';
import { queryClient } from '@wecasa/common/src/queries/query-client';
import colors from '@wecasa/common/src/utils/colors';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { toRem } from '@wecasa/common/src/utils/styles';
import { initialVariants } from '@wecasa/common/src/variants/initialVariants';

import { ModalProvider } from '@wecasa/web-ui/src/components/modal/modal-container-with-backdrop';
import ScreenLoader from '@wecasa/web-ui/src/components/screen-loader/screen-loader';
import ScrollToTop from '@wecasa/web-ui/src/components/scroll-to-top/scroll-to-top';
import { GlobalStyle } from '@wecasa/web-ui/src/styles/global-style';
import { minWidth } from '@wecasa/web-ui/src/styles/mixins';
import { Experiment, getDeviceIdFromAmplitudeCookie } from '@wecasa/web-ui/src/utils/amplitude-experiment';

import CleaningPromoModal from './components/promo-code/cleaning-promo-modal';
import Loader from './containers/loader/loader';
import MainContainer from './containers/main-container/main-container';
import { AppContextProvider } from './contexts/app-context';
import PaymentProvider from './contexts/payment-provider';
import { route } from './steps/steps';
import { intercomGlobalStyle } from './styles/intercom-global-style';
import { connectUserToExternalSystems } from './utils/authentication';
import config from './utils/config';

const headerHeight = toRem(56);

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white100};
  min-height: 100%;

  ${minWidth.md`
    min-height: calc(100% - ${headerHeight});
  `};
`;

if (import.meta.env.MODE === 'development' && !window.wecasa) {
  window.wecasa = {
    token: import.meta.env.VITE_REACT_APP_AUTH_TOKEN,
    mountpoint: route('root'),
    universe: import.meta.env.VITE_REACT_APP_UNIVERSE,
    siteUrl: import.meta.env.VITE_REACT_APP_SITE_URL,
    referer: import.meta.env.VITE_REACT_APP_REFERER,
    stripePublishableKey: import.meta.env.VITE_REACT_APP_STRIPE_PUBLISHABLE_KEY,
    admin_logged_in: import.meta.env.VITE_REACT_APP_IS_ADMIN_SIGN_IN,
    locale: import.meta.env.VITE_REACT_APP_LOCALE,
  };
}

queryClient.setDefaultOptions({
  queries: {
    ...queryClient.getDefaultOptions().queries,
    refetchOnWindowFocus: false,
  },
});

// We need to keep the Stripe promise in this file instead of PaymentProvider otherwise Stripe will never be loaded
const stripePromise = window?.wecasa?.stripePublishableKey ? loadStripe(window.wecasa.stripePublishableKey) : null;

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Root = () => (
  <SentryRoutes>
    <Route
      path='*'
      element={
        <>
          <ScrollToTop />
          <CleaningPromoModal />
          <MainContainer />
        </>
      }
    />
  </SentryRoutes>
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const router = sentryCreateBrowserRouter([{ path: '*', Component: Root }], { basename: window.wecasa.mountpoint });

type AppProps = {
  persistor: Persistor;
  store: Store;
};

const App = ({ store, persistor }: AppProps) => {
  const [deviceId, setDeviceId] = useState<string>();

  useEffect(() => {
    setDeviceId(getDeviceIdFromAmplitudeCookie(config.AMPLITUDE_API_KEY));
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={config.GOOGLE_CLIENT_ID}>
        <ReactQueryDevtools initialIsOpen={false} />
        <Provider store={store}>
          <PersistGate loading={<ScreenLoader />} persistor={persistor}>
            {deviceId && (
              <ExperimentProvider
                experimentClient={Experiment.initialize(config.AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY, {
                  automaticExposureTracking: false,
                  fetchOnStart: false, // fetch will happen in loader, on user ready (signed in / signed out)
                  initialVariants: initialVariants[getCurrentCountry()],
                  serverUrl: config.AMPLITUDE_EXPERIMENT_SERVER_URL,
                  flagsServerUrl: config.AMPLITUDE_FLAGS_SERVER_URL,
                })}
                deviceId={deviceId}
              >
                <PaymentProvider stripePromise={stripePromise}>
                  <GlobalStyle extraStyle={intercomGlobalStyle} />
                  <ModalProvider>
                    <AuthenticationContextProvider onAuthenticated={connectUserToExternalSystems}>
                      <ConfigProvider>
                        <UniversesProvider>
                          <AppContextProvider>
                            <Loader>
                              <AppContainer>
                                <RouterProvider router={router} />
                              </AppContainer>
                            </Loader>
                          </AppContextProvider>
                        </UniversesProvider>
                      </ConfigProvider>
                    </AuthenticationContextProvider>
                  </ModalProvider>
                </PaymentProvider>
              </ExperimentProvider>
            )}
          </PersistGate>
        </Provider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
};

export default App;
